import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Box, IconButton, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";
import { getBase64Image } from "../../utils/covertor";
import layoutCacheStore from "../../utils/Cache";
import { truncateText } from "../../utils/truncateText";
import { FLOOR_PLAN_PREVIEW_MAX_SCALE } from "../../constants/floorPlanPreview";
import { BENTO_SURFACE_BORDER_RADIUS } from "../UploadStates/uploadCardLayout";


function formatProjectStartedDate(createdAt) {
  if (createdAt == null || createdAt === "") return null;
  try {
    const d = new Date(createdAt);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

/**
 * Presentational card for a saved floor plan: preview image, name, count +
 * "Started" date, and a three-dot options button. Shared between the desktop
 * bento grid (`MainBentoCombinedLibraryBody`) and the mobile horizontal
 * slider (`MobileFloorPlanSlider`). The card itself fills its parent
 * (`width: 100%`); sizing for slider vs grid is the parent's responsibility.
 *
 * Label intentionally reads "Started" rather than "Uploaded" because the
 * same surface lists both originals and client copies, and `createdAt`
 * on a copy is the copy date, not the original upload date.
 */
function FloorPlanCard({ item, onClick, onDoubleClick, onMoreOptionsClick }) {
  const layoutDisplayCount = Math.max(item?.userSavedLayoutsCount - 1, 0);
  const layoutCountLabel =
    layoutDisplayCount === 1 ? "1 Layout" : `${layoutDisplayCount} Layouts`;
  const startedDateLabel = formatProjectStartedDate(item?.createdAt);


  const cachedData = layoutCacheStore.get(String(item?._id));
  let cachedImage = cachedData?.versions?.[item?.version];
  getBase64Image(cachedImage?.url)
    .then((isValid) => {
      if (!isValid) {
        cachedImage = null;
        const newCachedData = layoutCacheStore.get(String(item?._id));
        if (newCachedData) {
          if (!newCachedData.versions) {
            newCachedData.versions = {};
          }
          newCachedData.versions[item?.version] = null;
          layoutCacheStore.set(String(item?._id), newCachedData);
        }
      }
    })
    .catch((error) => {
      console.error("Error validating cached image:", error);
    });

  const previewSrc =
    cachedImage?.url ||
    item?.furnishedImageId ||
    item?.image ||
    item?.thumbnailFurnishedImage ||
    item?.thumbnailImage;

  return (
    <Box
      tabIndex={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minWidth: 0,
        flex: 1,
        border: "1px solid rgba(0,0,0,0.08)",
        p: 0,
        borderRadius: BENTO_SURFACE_BORDER_RADIUS,
        position: "relative",
        backgroundColor: "#FFFFFF",
        overflow: "hidden",
        cursor: "pointer",
        outline: "none",
        transition: "border-color 0.2s ease, background-color 0.2s ease",
        "&:hover": {
          borderColor: "rgba(0,0,0,0.12)",
          backgroundColor: "#F5F5F7",
        },
        "&:focus-visible": {
          borderColor: "rgba(32, 33, 36, 0.35)",
          boxShadow: "0 0 0 3px rgba(32, 33, 36, 0.12)",
        },
      }}
      onDoubleClick={onDoubleClick}
      onClick={onClick}
    >
      <Box
        className="floor-plan-card-preview"
        sx={{
          width: "100%",
          bgcolor: "#F5F5F7",
          transition: "background-color 0.2s ease",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          aspectRatio: "1 / 1",
          minHeight: 0,
          position: "relative",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box
          component="img"
          src={previewSrc}
          alt={item?.name ? `${item.name} preview` : "Floor plan preview"}
          loading="lazy"
          decoding="async"
          sx={{
            maxWidth: FLOOR_PLAN_PREVIEW_MAX_SCALE,
            maxHeight: FLOOR_PLAN_PREVIEW_MAX_SCALE,
            width: "auto",
            height: "auto",
            objectFit: "contain",
            display: "block",
            position: "relative",
            zIndex: 0,
            boxShadow:
              "0 1px 3px rgba(0,0,0,0.28), 0 0 1px rgba(0,0,0,0.2)",
            borderRadius: "2px",
          }}
        />
      </Box>

      {/* Bottom row: text left, options button right — +2px left inset vs horizontal padding */}
      <Box
        sx={{
          pl: "10px",
          pr: 1,
          pt: 1,
          pb: 1,
          width: "100%",
          minWidth: 0,
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-end",
          gap: 0.5,
          boxSizing: "border-box",
        }}
      >
        {/* Left: title + subtitle */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: "#545454",
              fontSize: "14px",
              fontWeight: 800,
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item?.name || truncateText(item?.payload?.prompt, 19) || "Untitled"}
          </Typography>
          <Typography
            component="div"
            sx={{
              color: "rgba(84, 84, 84, 0.62)",
              fontSize: "12px",
              fontWeight: 400,
              mt: 0.25,
              lineHeight: 1.35,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {startedDateLabel
              ? `${layoutCountLabel} · Started ${startedDateLabel}`
              : layoutCountLabel}
          </Typography>
        </Box>

        {/* Right: options button — same dimensions as MainHomeHeaderActions */}
        <IconButton
          aria-label="Floor plan options"
          size="small"
          sx={{
            flexShrink: 0,
            width: 36,
            height: 36,
            color: "#545454",
            bgcolor: "rgba(255,255,255,0.9)",
            border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            "&:hover": {
              bgcolor: "rgba(248,248,250,1)",
              borderColor: "rgba(0,0,0,0.12)",
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onMoreOptionsClick?.(item, e);
          }}
        >
          <MoreHorizIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

FloorPlanCard.propTypes = {
  item: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onMoreOptionsClick: PropTypes.func,
};

export default FloorPlanCard;
